import { FC, useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Box, Button, Typography, Link } from '@robocorp/components';
import { AccessCredentialsWithSecret } from '@robocorp/robocloud-internal-cds-sdk';

import { PartyAvatar } from '~/components';
import { withErrorBoundary } from '~/hocs';
import { useSelector, useLogout } from '~/hooks';
import { getAuthToken } from '~/utils/authHelpers';

import {
  createApiKey,
  getApiKeyById,
  editApiKey,
  getApiKeys,
} from '~/services/userAPI/personalApiKeyService';
import { validateApiResponse } from '~/queries/utils';

type Application = {
  name: string;
  protocol: string;
};

type ApplicationType = 'assistant' | 'agent' | 'automation-studio';

const applications: Record<ApplicationType, Application> = {
  assistant: {
    protocol: 'robocorpassistant',
    name: 'Robocorp Assistant',
  },

  agent: {
    protocol: 'robocorpagent',
    name: 'Robocorp Agent',
  },

  'automation-studio': {
    protocol: 'robocorpstudio',
    name: 'Robocorp Automation Studio',
  },
};

export const applicationTypes = Object.keys(applications) as ApplicationType[];

interface Props {
  applicationId: ApplicationType;
  // Both props below were added as part of the updated linking process
  // Read more in the PR
  type: string | null;
  hostname: string | null;
}

const isAssistantUserTokenLinking = ({
  applicationId,
  type,
}: {
  applicationId: ApplicationType;
  type: string | null;
}) => {
  if (applicationId !== 'assistant') {
    return false;
  }

  if (type !== 'credentials') {
    return false;
  }

  return true;
};

const upsertAssistantUserCredentials = async ({
  hostname,
}: {
  hostname: string | null;
}): Promise<AccessCredentialsWithSecret> => {
  const getUserCredentialsDetails = (params: { hostname: string | null }) => {
    const ASSISTANT_DEFAULT_HOSTNAME = 'default';

    const hostnameWithDefault = params.hostname ?? ASSISTANT_DEFAULT_HOSTNAME;

    return {
      name: `application-activation-assistant-${hostnameWithDefault}`,
      description: `These credentials were created automatically when linking the Robocorp Assistant for ${hostnameWithDefault}`,
    };
  };

  const { name, description } = getUserCredentialsDetails({ hostname });

  const userCredentialsList = validateApiResponse(await getApiKeys()).data ?? [];

  const existingUserToken = userCredentialsList.find(
    (userCredential) => userCredential.name === name,
  );

  if (existingUserToken) {
    const userTokenWithSecret = validateApiResponse(await getApiKeyById(existingUserToken.id)).data;

    if (userTokenWithSecret === undefined) {
      throw Error(
        `Unexpected error when retrieving the existing user access credentials (${existingUserToken.id})`,
      );
    }

    return userTokenWithSecret;
  }

  const createdUserToken = validateApiResponse(await createApiKey(name)).data;

  if (createdUserToken === undefined) {
    throw Error(`Unexpected error when creating the user access credentials`);
  }

  await editApiKey(createdUserToken.id, { name, description });

  const userTokenWithSecret = validateApiResponse(await getApiKeyById(createdUserToken.id)).data;

  if (userTokenWithSecret === undefined) {
    throw Error(
      `Unexpected error when retrieving the created user access credentials (${createdUserToken.id})`,
    );
  }

  return userTokenWithSecret;
};

/**
 * Robocorp application linking content
 */
const Content: FC<Props> = ({ applicationId, hostname, type }) => {
  const { t } = useTranslation();

  const [isLinking, setLinking] = useState<boolean>(false);

  const user = useSelector((state) => state.userResources.users.current);
  const onLogout = useLogout();

  const application = applications[applicationId];

  const onLinking = useCallback(async () => {
    setLinking(true);

    const getUserTokenDeepLink = async () => {
      const userTokenWithSecret = await upsertAssistantUserCredentials({ hostname });

      return `${application.protocol}://link?identifier=${userTokenWithSecret.id}&secret=${userTokenWithSecret.secret}`;
    };

    const getJWTTokenDeepLink = async () => {
      const token = await getAuthToken();
      const apiURL = import.meta.env.VITE_INTEGRATIONS_BASE_URL;

      return `${application?.protocol}://link?token=${token}&apiURL=${encodeURIComponent(apiURL)}`;
    };

    window.location.href = isAssistantUserTokenLinking({ applicationId, type })
      ? await getUserTokenDeepLink()
      : await getJWTTokenDeepLink();

    setTimeout(() => {
      setLinking(false);
    }, 6000);
  }, [application, hostname]);

  if (!application) {
    return null;
  }

  return (
    <Box
      display="flex"
      width="100%"
      height="100%"
      alignItems="center"
      flexDirection="column"
      py="$64"
    >
      <Box
        borderRadius="$6"
        backgroundColor="grey90"
        py="$48"
        px="$48"
        minWidth={420}
        margin="auto"
        textAlign="center"
        boxShadow="small"
      >
        <Box maxWidth={320} marginX="auto">
          <img
            src={`/app-logos/${applicationId}.svg`}
            alt={application.name}
            width={64}
            height={64}
          />
          <Typography fontSize="$36" fontWeight="semibold" fontFamily="title" mt="$32">
            {t('linking.linkAccount')}
          </Typography>
          <Typography fontSize="$14" mb="$48" color="grey20" mt="$24" lineHeight="$165">
            {t(`linking.descriptions.${application.name}`)}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" textAlign="left" mb="$24">
          <Box mr="$12" height={32}>
            <PartyAvatar size="medium" party={user} />
          </Box>
          <Box>
            <Typography as="strong" ml="$4" fontWeight="semibold">
              {`${user.details.first_name} ${user.details.last_name}`}
            </Typography>
            <Typography ml="$4">{user.details.email}</Typography>
          </Box>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="stretch"
          marginX="auto"
          width={240}
          maxWidth="100%"
        >
          <Button loading={isLinking} onClick={onLinking} size="medium">
            {t('linking.linkAccount')}
          </Button>
        </Box>
        <Box mt="$12">
          <Button size="small" variant="ghost" onClick={onLogout}>
            <Typography color="grey30">{t('linking.useDifferentAccount')}</Typography>
          </Button>
        </Box>

        <Typography lineHeight="$165" fontSize="$11" mt="$64" color="grey30">
          <Trans i18nKey="linking.troubleshoot" t={t}>
            Refer to <Link href="https://robocorp.com/docs/faq/linking">manual linking guide</Link>{' '}
            or
            <Link href="mailto:support@robocorp.com">contact us</Link> if you have trouble linking.
          </Trans>
        </Typography>
      </Box>
    </Box>
  );
};

export const ApplicationLink = withErrorBoundary(Content);
